import { useState } from "react";
import { motion } from "framer-motion";
import { Brain, CheckCircle2, XCircle, ArrowRight, Check } from "lucide-react";
import { cls } from "../../lib/cls";

interface MemoryDeckProps {
  onApply?: (cardId: string, title: string) => void;
}

interface Card {
  id: string;
  title: string;
  pattern: string;
  hits: number;
  rejects: number;
  savings: string;
  applies: string;
  dataset?: string;
}

const CARDS: Card[] = [
  {
    id: "mc1",
    title: "Substitute 90-day chandeliers",
    pattern: "SKU.leadDays > Milestone.dueIn − 14",
    hits: 7,
    rejects: 1,
    savings: "₹6.4 L recovered across 7 projects",
    applies: "applies to: 1 SKU in current project",
    dataset: "Kaggle #7 vendor SLA",
  },
  {
    id: "mc2",
    title: "Kitchen budget rebalance",
    pattern: "BudgetLine.share > 25% on Room=kitchen",
    hits: 12,
    rejects: 0,
    savings: "−18% kitchen over-spend, on average",
    applies: "applies to: kitchen module match",
    dataset: "Kaggle #9 construction PM",
  },
  {
    id: "mc3",
    title: "Mughal-pillar accent ceiling",
    pattern: "Intent.styleCosine(mughal) > 0.6 & Room.ceilingHt ≥ 10ft",
    hits: 4,
    rejects: 2,
    savings: "+11% client NPS on premium projects",
    applies: "applies to: 1 living room (10.5ft ceiling)",
    dataset: "Kaggle #2 international styles",
  },
  {
    id: "mc4",
    title: "Pre-render style score",
    pattern: "Render request when Σ Placement.styleMatch < 0.78",
    hits: 19,
    rejects: 3,
    savings: "$650/project · 2.4 fewer re-renders",
    applies: "watches every Take Render call",
  },
  {
    id: "mc5",
    title: "RERA filing approval packet",
    pattern: "Milestone.kind = handover & Approval.regulator missing",
    hits: 6,
    rejects: 0,
    savings: "−18 days handover slip avoided",
    applies: "auto-fills on every new project",
    dataset: "Kaggle #8 Gujarat RERA",
  },
  {
    id: "mc6",
    title: "Accessibility cue (older clients)",
    pattern: "Client.persona ∋ {senior} → require Grab-bar / ramp",
    hits: 3,
    rejects: 0,
    savings: "0 misfits since promotion (Q1)",
    applies: "applies on intake brief",
  },
];

export function MemoryDeck({ onApply }: MemoryDeckProps): JSX.Element {
  const [applied, setApplied] = useState<Set<string>>(new Set());
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {CARDS.map((c, i) => {
        const accRatio = c.hits / Math.max(1, c.hits + c.rejects);
        const isApplied = applied.has(c.id);
        return (
          <motion.div
            key={c.id}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: i * 0.04 }}
            className={cls("panel p-4 flex flex-col gap-2 transition-all", isApplied ? "ring-1 ring-fern/40" : "")}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-ink-950 text-cream grid place-items-center">
                  <Brain className="w-3.5 h-3.5" />
                </span>
                <span className="text-[10px] uppercase tracking-wider text-accent">Promoted</span>
              </div>
              <span className="pill bg-foyr/10 text-foyr-deep tabular">
                {Math.round(accRatio * 100)}% accept
              </span>
            </div>
            <div className="text-sm font-semibold text-ink-950">{c.title}</div>
            <div className="font-mono text-[11px] text-ink-700 leading-snug bg-cream-deeper rounded-lg px-2 py-1.5">
              {c.pattern}
            </div>
            <div className="grid grid-cols-2 gap-1 text-[11px]">
              <span className="inline-flex items-center gap-1 text-fern"><CheckCircle2 className="w-3 h-3" /> {c.hits} accepts</span>
              <span className="inline-flex items-center gap-1 text-ink-500"><XCircle className="w-3 h-3" /> {c.rejects} rejects</span>
            </div>
            <div className="text-xs text-ink-700 mt-auto">{c.savings}</div>
            <div className="flex items-center justify-between text-[10px] text-ink-500">
              <span className="truncate">{c.applies}</span>
              {c.dataset ? <span className="font-mono opacity-60">{c.dataset}</span> : null}
            </div>
            <button
              onClick={() => {
                setApplied((cur) => new Set([...cur, c.id]));
                onApply?.(c.id, c.title);
              }}
              className={cls("btn !text-xs !py-1.5 mt-1 justify-between", isApplied ? "btn-foyr" : "btn-outline")}
            >
              <span className="inline-flex items-center gap-1.5">
                {isApplied ? <Check className="w-3 h-3" /> : null}
                {isApplied ? "Applied · watching" : "Apply to project"}
              </span>
              <ArrowRight className="w-3 h-3" />
            </button>
          </motion.div>
        );
      })}
    </div>
  );
}
